import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate } from 'react-router-dom';
import { Shield, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';

const AdminCreator = () => {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [details, setDetails] = useState('');
  const navigate = useNavigate();

  const createAdminManually = async () => {
    setLoading(true);
    setError('');
    setDetails('Starting manual admin creation...');

    try {
      const adminEmail = 'vladimir.bunic@convergedsecurity.es';
      const adminPassword = 'Vbunic@1720';

      setDetails('Step 1: Checking if admin user already exists...');
      
      // First, try to sign in to see if user already exists
      const { data: existingLogin, error: existingError } = await supabase.auth.signInWithPassword({
        email: adminEmail,
        password: adminPassword
      });

      if (existingLogin.user) {
        setDetails('Admin user already exists! Setting up profile...');
        
        // User exists, just ensure profile is set up
        const { error: profileError } = await supabase
          .from('member_profiles_2025_12_03_10_30')
          .upsert({
            user_id: existingLogin.user.id,
            full_name: 'Vladimir Bunic',
            email: adminEmail,
            organization: 'Converged Security Institute',
            position: 'Administrator',
            membership_status: 'active',
            payment_status: 'paid',
            is_admin: true
          }, {
            onConflict: 'user_id'
          });

        if (profileError) {
          console.error('Profile error:', profileError);
          setDetails('Profile setup had issues, but login works.');
        } else {
          setDetails('Admin profile updated successfully!');
        }

        await supabase.auth.signOut();
        setSuccess(true);
        return;
      }

      // User doesn't exist, create it
      setDetails('Step 2: Creating new admin user...');
      
      const { data: signupData, error: signupError } = await supabase.auth.signUp({
        email: adminEmail,
        password: adminPassword,
        options: {
          data: {
            full_name: 'Vladimir Bunic'
          }
        }
      });

      if (signupError) {
        if (signupError.message.includes('already registered')) {
          setDetails('User already registered, trying to login...');
          
          // Try login again
          const { data: loginData, error: loginError } = await supabase.auth.signInWithPassword({
            email: adminEmail,
            password: adminPassword
          });

          if (loginError) {
            throw new Error(`Cannot login to existing account: ${loginError.message}`);
          }

          setDetails('Logged in successfully! Setting up profile...');
          
          const { error: profileError } = await supabase
            .from('member_profiles_2025_12_03_10_30')
            .upsert({
              user_id: loginData.user.id,
              full_name: 'Vladimir Bunic',
              email: adminEmail,
              organization: 'Converged Security Institute',
              position: 'Administrator',
              membership_status: 'active',
              payment_status: 'paid',
              is_admin: true
            }, {
              onConflict: 'user_id'
            });

          if (profileError) {
            console.error('Profile error:', profileError);
            setDetails('Profile setup had issues, but login works.');
          }

          await supabase.auth.signOut();
          setSuccess(true);
          return;
        } else {
          throw new Error(`Signup failed: ${signupError.message}`);
        }
      }

      if (signupData.user) {
        setDetails('Step 3: User created! Setting up profile...');
        
        // Wait a moment for the user to be fully created
        await new Promise(resolve => setTimeout(resolve, 2000));
        
        // Try to login with the new user
        const { data: loginData, error: loginError } = await supabase.auth.signInWithPassword({
          email: adminEmail,
          password: adminPassword
        });

        if (loginError) {
          setDetails('User created but login failed. This might be due to email verification requirement.');
          setSuccess(true); // Still consider it success since user was created
          return;
        }

        setDetails('Step 4: Creating admin profile...');
        
        const { error: profileError } = await supabase
          .from('member_profiles_2025_12_03_10_30')
          .insert({
            user_id: loginData.user.id,
            full_name: 'Vladimir Bunic',
            email: adminEmail,
            organization: 'Converged Security Institute',
            position: 'Administrator',
            membership_status: 'active',
            payment_status: 'paid',
            is_admin: true
          });

        if (profileError) {
          console.error('Profile creation error:', profileError);
          setDetails('User created but profile setup had issues. You can still try to login.');
        } else {
          setDetails('Admin profile created successfully!');
        }

        await supabase.auth.signOut();
      }

      setDetails('Admin account setup complete!');
      setSuccess(true);

    } catch (error: any) {
      console.error('Admin creation error:', error);
      setError(error.message);
      setDetails('Failed to create admin account');
    } finally {
      setLoading(false);
    }
  };

  // Auto-create on component mount
  useEffect(() => {
    createAdminManually();
  }, []);

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background to-secondary flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CheckCircle className="h-12 w-12 text-green-600 mx-auto mb-4" />
            <CardTitle>✅ Admin Account Ready!</CardTitle>
            <CardDescription>
              The administrator account has been set up.
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center space-y-4">
            <div className="p-4 bg-green-50 rounded-lg">
              <p className="text-sm font-medium">🎉 Admin Login Credentials:</p>
              <p className="text-sm text-muted-foreground">📧 Email: vladimir.bunic@convergedsecurity.es</p>
              <p className="text-sm text-muted-foreground">🔑 Password: Vbunic@1720</p>
            </div>
            <div className="p-4 bg-blue-50 rounded-lg">
              <p className="text-xs text-muted-foreground">
                ✅ Account setup completed! Try logging in now.
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                📧 If you receive an email verification link, please click it to confirm your email.
              </p>
            </div>
            <div className="flex flex-col gap-2">
              <Button onClick={() => navigate('/admin-login')} className="w-full">
                🚀 Go to Admin Login
              </Button>
              <Button variant="outline" onClick={() => navigate('/')} className="w-full">
                🏠 Back to Home
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background to-secondary flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <AlertCircle className="h-12 w-12 text-red-600 mx-auto mb-4" />
            <CardTitle>❌ Admin Creation Issue</CardTitle>
            <CardDescription>
              There was an issue creating the admin account.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
            <div className="p-4 bg-gray-50 rounded-lg">
              <p className="text-xs text-muted-foreground">{details}</p>
            </div>
            <div className="p-4 bg-blue-50 rounded-lg">
              <p className="text-xs text-muted-foreground">
                💡 Try logging in anyway with: vladimir.bunic@convergedsecurity.es / Vbunic@1720
              </p>
            </div>
            <div className="flex flex-col gap-2">
              <Button onClick={() => navigate('/admin-login')} className="w-full">
                🚀 Try Admin Login
              </Button>
              <Button onClick={createAdminManually} disabled={loading} className="w-full" variant="outline">
                🔄 Try Again
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-secondary flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <Loader2 className="h-12 w-12 text-primary mx-auto mb-4 animate-spin" />
          <CardTitle>🔧 Setting Up Admin Account</CardTitle>
          <CardDescription>
            Please wait while we configure the administrator account...
          </CardDescription>
        </CardHeader>
        <CardContent className="text-center space-y-4">
          <div className="p-4 bg-blue-50 rounded-lg">
            <p className="text-sm font-medium">Configuring:</p>
            <p className="text-sm text-muted-foreground">📧 vladimir.bunic@convergedsecurity.es</p>
            <p className="text-sm text-muted-foreground">🔑 Password: Vbunic@1720</p>
          </div>
          <div className="p-4 bg-gray-50 rounded-lg">
            <p className="text-xs text-muted-foreground">{details}</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminCreator;